"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.applyNetlistToProject = applyNetlistToProject;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const sigma_engine_shared_2 = require("@cadstrom/sigma-engine-shared");
function applyNetlistToProject(projectModel, netlistModel) {
    const netlistMigrationResult = {
        type: sigma_engine_shared_2.DesignModels.MigrationResultType.AltiumProjectTransformation,
        message: 'Applied netlist to project',
        innerResults: [],
        isModified: false,
        entity: projectModel,
    };
    try {
        if (!netlistModel) {
            throw new Error('Netlist model is null or undefined');
        }
        const validationResult = validateNetlistCompatibility(projectModel, netlistModel);
        if (validationResult.type === 'error') {
            return {
                type: sigma_engine_shared_2.DesignModels.MigrationResultType.AltiumProjectTransformation,
                message: validationResult.message,
                innerResults: [validationResult],
                isModified: false,
                severity: sigma_engine_shared_2.DesignModels.BoundaryLevel.Error,
                entity: null,
            };
        }
        const workingModel = structuredClone(projectModel);
        const updateResult = updateNetsFromNetlist(workingModel, netlistModel);
        netlistMigrationResult.innerResults.push(updateResult);
        netlistMigrationResult.entity = workingModel;
        netlistMigrationResult.isModified = true;
        const successResult = {
            type: 'info',
            message: 'Successfully applied netlist to project model',
        };
        netlistMigrationResult.innerResults.push(successResult);
    }
    catch (error) {
        const errorResult = {
            type: 'error',
            severity: sigma_engine_shared_2.DesignModels.BoundaryLevel.Error,
            message: `Failed to apply netlist to project: ${error instanceof Error ? error.message : String(error)}`,
            stack: error instanceof Error ? error.stack : undefined,
        };
        netlistMigrationResult.innerResults.push(errorResult);
        netlistMigrationResult.message = errorResult.message;
        netlistMigrationResult.entity = null;
        netlistMigrationResult.isModified = false;
    }
    (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(netlistMigrationResult);
    return netlistMigrationResult;
}
function validateNetlistCompatibility(projectModel, netlistModel) {
    const netlistModuleNames = new Set(netlistModel.modules.map((m) => m.name));
    const projectModuleNames = new Set();
    const hasSchematicDesigns = Object.values(projectModel.designs).some((design) => design.modelType === 'schematic');
    for (const module of projectModel.modules) {
        if (module.name.trim() === '') {
            continue;
        }
        const inSchematic = module.instances.find(({ designId }) => projectModel.designs[designId]?.modelType === 'schematic');
        if (!inSchematic) {
            continue;
        }
        projectModuleNames.add(module.name);
    }
    if (hasSchematicDesigns) {
        const missingModules = [];
        for (const netlistModuleName of netlistModuleNames) {
            if (!projectModuleNames.has(netlistModuleName)) {
                missingModules.push(netlistModuleName);
            }
        }
        if (missingModules.length > 0) {
            return {
                type: 'error',
                message: `Netlist contains modules not found in project model: ${missingModules.join(', ')}`,
            };
        }
        const expectedModules = [];
        for (const projectModuleName of projectModuleNames) {
            if (netlistModuleNames.has(projectModuleName)) {
                continue;
            }
            const projectModule = projectModel.modules.find((m) => m.name === projectModuleName);
            if (!projectModule) {
                throw new Error(`Missing project module ${projectModuleName}`);
            }
            const component = projectModel.components[projectModule.componentRef];
            if (!component) {
                throw new Error(`Missing component ${projectModule.componentRef} for module ${projectModule.name}`);
            }
            if (!component.isVirtual && component.numPads !== 0 && component.numPins !== 0) {
                expectedModules.push(projectModuleName);
            }
        }
        if (expectedModules.length > 0) {
            return {
                type: 'error',
                message: `Project model contains non-virtual modules not found in netlist: ${expectedModules.join(', ')}`,
            };
        }
    }
    return {
        type: 'info',
        message: 'Netlist compatibility validation passed',
    };
}
function updateNetsFromNetlist(projectModel, netlistModel) {
    const result = {
        type: 'info',
        message: 'Updated nets from netlist',
        innerResults: [],
    };
    projectModel.globalNets = {};
    for (const design of Object.values(projectModel.designs)) {
        if (design.modelType !== 'schematic') {
            continue;
        }
        try {
            updateDesignNetsFromNetlist(design, netlistModel, result);
        }
        catch (error) {
            result.innerResults.push({
                type: 'error',
                severity: sigma_engine_shared_2.DesignModels.BoundaryLevel.Error,
                message: `Failed to apply netlist to design ${design.designId}: ${error instanceof Error ? error.message : String(error)}`,
            });
        }
    }
    (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(result);
    return result;
}
function updateDesignNetsFromNetlist(design, netlistModel, result) {
    const pendingMessages = [];
    const netNameMap = new Map();
    for (const module of design.modules) {
        const moduleName = module.fullModuleName ?? module.name;
        const inNetlist = netlistModel.modules.some((m) => m.name === moduleName);
        if (!inNetlist) {
            continue;
        }
        for (const [pinRef, netName] of Object.entries(module.pinNets)) {
            const existingNetName = netName.valueOf();
            const netlistModule = netlistModel.modules.find((m) => m.name === moduleName);
            if (!netlistModule) {
                throw new Error(`Missing netlist module ${moduleName}`);
            }
            let netlistNetName = netlistModule.pinNets[pinRef]?.valueOf();
            if (netlistNetName === undefined) {
                const pinRefWithUnderscores = Object.keys(netlistModule.pinNets).find((k) => k.replace(/ /g, '_') === pinRef);
                if (pinRefWithUnderscores !== undefined) {
                    netlistNetName = netlistModule.pinNets[pinRefWithUnderscores];
                }
                if (netlistNetName === undefined) {
                    pendingMessages.push({
                        type: 'error',
                        message: `Missing netlist net for pin ${pinRef} on module ${moduleName}`,
                    });
                    continue;
                }
            }
            if (netNameMap.has(existingNetName)) {
                if (netNameMap.get(existingNetName) !== netlistNetName) {
                    throw new Error(`Net name mismatch for pin ${pinRef} on module ${moduleName}: ${netNameMap.get(existingNetName)} vs ${netlistNetName} (${existingNetName})`);
                }
            }
            else {
                netNameMap.set(existingNetName, netlistNetName);
                if (existingNetName !== netlistNetName) {
                    pendingMessages.push({
                        type: 'net-name-updated',
                        message: `Net name updated from ${existingNetName} to ${netlistNetName} on module ${moduleName} pin ${pinRef}`,
                        oldNetName: existingNetName,
                        newNetName: netlistNetName,
                    });
                }
            }
        }
    }
    const oldNets = design.nets;
    const newNets = {};
    const pinNetWrites = [];
    for (const [oldNetName, newNetName] of netNameMap.entries()) {
        if (!(oldNetName in oldNets)) {
            throw new Error(`Missing net ${oldNetName} in design ${design.designId}`);
        }
        if (newNetName in newNets) {
            newNets[newNetName] = {
                ...newNets[newNetName],
                ...oldNets[oldNetName],
                name: newNetName,
                connections: newNets[newNetName].connections,
            };
            for (const connection of oldNets[oldNetName].connections) {
                if (!newNets[newNetName].connections.some((c) => c.moduleRef === connection.moduleRef && c.pinRef === connection.pinRef)) {
                    newNets[newNetName].connections.push(connection);
                    const module = design.modules.find((m) => m.ref === connection.moduleRef);
                    if (!module) {
                        throw new Error(`Missing module ${connection.moduleRef} in design ${design.designId}`);
                    }
                    pinNetWrites.push({ module, pinRef: connection.pinRef, netName: newNetName });
                }
            }
        }
        else {
            newNets[newNetName] = {
                ...oldNets[oldNetName],
                name: newNetName,
                connections: [...oldNets[oldNetName].connections],
                isGlobal: true,
            };
            for (const connection of newNets[newNetName].connections) {
                const module = design.modules.find((m) => m.ref === connection.moduleRef);
                if (!module) {
                    throw new Error(`Missing module ${connection.moduleRef} in design ${design.designId}`);
                }
                pinNetWrites.push({ module, pinRef: connection.pinRef, netName: newNetName });
            }
        }
    }
    design.nets = newNets;
    for (const write of pinNetWrites) {
        write.module.pinNets[write.pinRef] = write.netName;
    }
    result.innerResults.push(...pendingMessages);
    for (const module of design.modules) {
        for (const [pinRef, netName] of Object.entries(module.pinNets)) {
            if (!(netName in design.nets)) {
                design.nets[netName] = {
                    ...oldNets[netName],
                    name: netName,
                    connections: [],
                };
            }
            const netInfo = design.nets[netName];
            if (!netInfo.connections.some((c) => c.moduleRef === module.ref && c.pinRef === pinRef)) {
                netInfo.connections.push({
                    moduleRef: module.ref,
                    moduleName: module.name,
                    pinRef,
                });
            }
        }
    }
}
function updateGlobalNets(projectModel, netlistModel) {
    try {
        const powerGroundPatterns = [
            /^VCC$/i,
            /^VDD$/i,
            /^VEE$/i,
            /^VSS$/i,
            /^VDDA$/i,
            /^VSSA$/i,
            /^GND$/i,
            /^GROUND$/i,
            /^AGND$/i,
            /^DGND$/i,
            /^PGND$/i,
            /^[+-]?\d+V\d*$/i,
            /^V\w*_\w+$/i,
        ];
        let globalNetCount = 0;
        for (const [netName, netInfo] of Object.entries(netlistModel.nets)) {
            const isGlobal = netInfo.isGlobal || powerGroundPatterns.some((pattern) => pattern.test(netName));
            if (isGlobal) {
                projectModel.globalNets[netName] = {
                    name: netName,
                };
                globalNetCount++;
            }
        }
        return {
            type: 'info',
            message: `Updated ${globalNetCount} global nets`,
        };
    }
    catch (error) {
        return {
            type: 'error',
            message: `Failed to update global nets: ${error instanceof Error ? error.message : String(error)}`,
        };
    }
}
//# sourceMappingURL=applyNetlistToProject.js.map